import AllOutOutlinedIcon from '@mui/icons-material/AllOutOutlined';
import ExpandOutlinedIcon from '@mui/icons-material/ExpandOutlined';
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined';
import HeightOutlinedIcon from '@mui/icons-material/HeightOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import TagIcon from '@mui/icons-material/Tag';
import {Grid2} from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type {SxProps} from '@mui/system';
import {lighten} from '@mui/system/colorManipulator';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import LabelChip from '@/components/common/LabelChip';
import Title from '@/components/common/Title';
import type {ForestMassSpecies} from '@/domain/entities/ForestMassSpecies';
import type {InventoryPlot} from '@/domain/entities/InventoryPlot';
import type {Species} from '@/domain/entities/Species';
import {primaryColor} from '@/theme/theme';

//TYPES
type Props = {
  species: Array<Species>;
  forestMassSpecies?: Array<ForestMassSpecies>;
  inventoryPlot: InventoryPlot;
};

const InventoryPlotInfo: FC<Props> = ({species, forestMassSpecies, inventoryPlot}) => {
  const {t} = useTranslation();

  const {code, forestMassSpeciesUuid, diameter, altitude, hrms, vrms, status} = inventoryPlot;

  const speciesUuid = forestMassSpecies?.find((fms) => fms.uuid === forestMassSpeciesUuid)?.speciesUuid;
  const speciesLabel = species.find((s) => s.uuid === speciesUuid)?.nameKey;

  return (
    <>
      <Stack>
        <Title size="h6" label={t('inventoryPlots.info')} />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Grid2 container spacing={1}>
          <Grid2 size={{xs: 12, sm: 6, md: 4}}>
            <LabelChip value={t('inventoryPlots.code')} icon={TagIcon} width={LABEL_WIDTH}>
              <Typography variant="body2" sx={styles.value}>
                {code}
              </Typography>
            </LabelChip>
          </Grid2>

          <Grid2 size={{xs: 12, sm: 6, md: 4}}>
            <LabelChip value={t('inventoryPlots.species')} icon={ForestOutlinedIcon} width={LABEL_WIDTH}>
              <Typography variant="body2" sx={styles.value}>
                {speciesLabel}
              </Typography>
            </LabelChip>
          </Grid2>

          <Grid2 size={{xs: 12, sm: 6, md: 4}}>
            <LabelChip value={t('inventoryPlots.diameter')} icon={StraightenOutlinedIcon} width={LABEL_WIDTH}>
              <Typography variant="body2" sx={styles.value}>
                {diameter}
              </Typography>
            </LabelChip>
          </Grid2>

          <Grid2 size={{xs: 12, sm: 6, md: 4}}>
            <LabelChip value={t('inventoryPlots.altitude')} icon={ExpandOutlinedIcon} width={LABEL_WIDTH}>
              <Typography variant="body2" sx={styles.value}>
                {altitude}
              </Typography>
            </LabelChip>
          </Grid2>

          <Grid2 size={{xs: 12, sm: 6, md: 4}}>
            <LabelChip value={t('inventoryPlots.hrms')} icon={AllOutOutlinedIcon} width={LABEL_WIDTH}>
              <Typography variant="body2" sx={styles.value}>
                {hrms}
              </Typography>
            </LabelChip>
          </Grid2>

          <Grid2 size={{xs: 12, sm: 6, md: 4}}>
            <LabelChip value={t('inventoryPlots.vrms')} icon={HeightOutlinedIcon} width={LABEL_WIDTH}>
              <Typography variant="body2" sx={styles.value}>
                {vrms}
              </Typography>
            </LabelChip>
          </Grid2>
        </Grid2>
        <Stack sx={styles.statusContainer}>
          <Typography variant="overline" sx={{fontWeight: 700}}>
            {status}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};
export default InventoryPlotInfo;

const LABEL_WIDTH = '160px';

const styles: Record<string, SxProps> = {
  statusContainer: {
    bgcolor: lighten(primaryColor, 0.95),
    p: 1,
    border: `1px solid ${lighten(primaryColor, 0.8)}`,
  },
  value: {
    pt: 1,
    fontWeight: 700,
  },
};
